import { Service } from '@features/service/entity';
import { VisitDetail } from '../../visits/entity';
import { OverheadCost } from './components/MonthlyTrendChart';

export type MetricsDateRange = {
  from: Date;
  to: Date;
};

export type BusinessMetricsInput = {
  visits: VisitDetail[];
  overheadCosts: OverheadCost[];
  dateRange: MetricsDateRange;
};

export type CustomerRetentionMetrics = {
  newCustomers: number;
  returningCustomers: number;
  totalCustomers: number;
  newCustomersPercentage: number;
  returningCustomersPercentage: number;
};

export type BreakEvenMetrics = {
  fixedCosts: number;
  variableCosts: number;
  currentRevenue: number;
  currentProfit: number;
  missingProfit: number;
};

export type PriceIncreaseMetrics = {
  requiredRevenue: number;
  currentRevenue: number;
  revenueDifference: number;
  totalServicesPerformed: number;
  averagePriceIncrease: number;
  priceIncreasePercentage: number;
  serviceSpecificIncreases: {
    serviceId: string;
    serviceName: string;
    currentPrice: number;
    suggestedPrice: number;
    increaseAmount: number;
    increasePercentage: number;
    timesPerformed: number;
  }[];
};

export type ProfitPerHourMetrics = {
  serviceId: string;
  serviceName: string;
  price: number;
  variableCost: number;
  profit: number;
  durationMinutes: number;
  durationHours: number;
  profitPerHour: number;
  timesPerformed: number;
  totalProfit: number;
};

export type VariableCostMetrics = {
  serviceId: string;
  serviceName: string;
  variableCost: number;
  timesPerformed: number;
  totalVariableCost: number;
  price: number;
  profitMargin: number;
  profitMarginPercentage: number;
};

export type ProfitPerHourExplicit = {
  serviceId: string;
  serviceName: string;
  profit: number;
  profitPerHour: number;
  durationMinutes: number;
  price: number;
  variableCost: number;
  timesPerformed: number;
  totalProfit: number;
  totalRevenue: number;
};

export type ServiceDetailedMetrics = {
  serviceId: string;
  serviceName: string;
  currentPrice: number;
  averageActualPrice: number;
  currentTimesPerformed: number;
  durationMinutes: number;

  averageVariableCost: number;
  contributionMargin: number;
  contributionMarginPercentage: number;
  profitPerHour: number;
  totalProfit: number;

  individual: {
    missingContributionIndividual: number;
    priceIncreaseIndividual: number;
    priceIncreaseIndividualPercentage: number;
    additionalVisitsIndividualNeeded: number;
    breakEvenPriceIndividual: number;
    breakEvenTotalVisitsIndividual: number;
  };

  revenueShare: number;
  profitShare: number;
  timeShare: number;
};

export type InefficiencyAnalysis = {
  serviceId: string;
  serviceName: string;
  timeSpentMinutes: number;
  timeSharePercentage: number;
  profit: number;
  profitSharePercentage: number;
  inefficiencyPercentage: number;
  financialLoss: number;
  timesPerformed: number;
  profitPerHour: number;
  recommendation: 'optimal' | 'acceptable' | 'inefficient' | 'very-inefficient';
};

export type LoyaltyPointsReport = {
  totalRedeemedValue: number;
  totalEarnedValue: number;
  totalRevenue: number;
  redeemedToRevenueRatio: number;
  visitsWithPointsRedeemed: number;
  totalVisits: number;
  visitsWithPointsRedeemedPercentage: number;
  averagePointsRedeemedPerVisit: number;
  averagePointsEarnedPerVisit: number;
  netLoyaltyImpact: number;
};

export type BusinessMetrics = {
  breakEven: BreakEvenMetrics;
  priceIncrease: PriceIncreaseMetrics;
  profitPerHour: ProfitPerHourMetrics[];
  variableCosts: VariableCostMetrics[];
  profitPerHourExplicit: ProfitPerHourExplicit[];
  inefficiencyAnalysis: InefficiencyAnalysis[];
  loyaltyPoints: LoyaltyPointsReport;
  serviceDetailedMetrics: ServiceDetailedMetrics[];
};

type ServiceStats = Map<
  string,
  {
    service: { id: string; name: string; price: number; durationMinutes: number };
    count: number;
    totalVariableCost: number;
    totalRevenue: number;
    totalTimeMinutes: number;
  }
>;

export const normalizeToMonthly = (
  amount: number,
  period: 'monthly' | 'yearly' | 'quarterly',
): number => {
  switch (period) {
    case 'monthly':
      return amount;
    case 'yearly':
      return amount / 12;
    case 'quarterly':
      return amount / 3;
    default:
      return amount;
  }
};

export const calculateServiceDuration = (service: Service): number => {
  return service.blocks.reduce((total, block) => {
    return total + block.duration;
  }, 0);
};

export const calculateVisitVariableCosts = (visit: Pick<VisitDetail, 'procedures'>): number => {
  const { procedures } = visit;
  return procedures.reduce((total, procedure) => {
    const stockCosts = procedure.stockAllowances.reduce((sum, allowance) => {
      return sum + Number(allowance.costs);
    }, 0);
    return total + stockCosts;
  }, 0);
};

export const getServiceFromVisit = (
  service: Service,
): { id: string; name: string; price: number; durationMinutes: number } => {
  return {
    id: service.id,
    name: service.serviceName,
    price: service.basePrice,
    durationMinutes: calculateServiceDuration(service),
  };
};

export const calculateFixedCosts = (overheadCosts: OverheadCost[]): number => {
  return overheadCosts.reduce(
    (total, cost) => total + normalizeToMonthly(cost.amount, cost.period),
    0,
  );
};

export const processVisitsAndCalculateStats = (filteredVisits: VisitDetail[]) => {
  let totalVariableCosts = 0;
  let totalRevenue = 0;
  let totalPointsEarned = 0;
  let totalPointsRedeemed = 0;

  const serviceStats: ServiceStats = new Map();

  filteredVisits.forEach(({ visit, loyalty, financials, services, procedures }) => {
    const visitVariableCosts = calculateVisitVariableCosts({ procedures });
    totalVariableCosts += visitVariableCosts;

    totalPointsEarned += loyalty.loyaltyPointsGained || 0;
    totalPointsRedeemed += loyalty.loyaltyPointsUsed || 0;
    totalRevenue +=
      Number(financials.paidPrice || 0) -
      Number(loyalty.loyaltyPointsUsed || 0) +
      Number(financials.totalSalesRevenue || 0);

    const visitTotalListPrice = services.reduce((sum, vs) => {
      return sum + getServiceFromVisit(vs).price;
    }, 0);

    services.forEach(service => {
      const serviceData = getServiceFromVisit(service);

      const visitTotalTime = services.reduce((sum, vs) => {
        return sum + getServiceFromVisit(vs).durationMinutes;
      }, 0);

      const serviceVariableCost =
        visitTotalTime > 0
          ? (visitVariableCosts * serviceData.durationMinutes) / visitTotalTime
          : 0;

      const actualPaidPrice =
        Number(financials.paidPrice || 0) - Number(loyalty.loyaltyPointsUsed || 0);
      const serviceRevenue =
        visitTotalListPrice > 0
          ? (actualPaidPrice * serviceData.price) / visitTotalListPrice
          : serviceData.price;

      const existingStats = serviceStats.get(serviceData.id);

      if (existingStats) {
        existingStats.count += 1;
        existingStats.totalVariableCost += serviceVariableCost;
        existingStats.totalRevenue += serviceRevenue;
        existingStats.totalTimeMinutes += serviceData.durationMinutes;
      } else {
        serviceStats.set(serviceData.id, {
          service: serviceData,
          count: 1,
          totalVariableCost: serviceVariableCost,
          totalRevenue: serviceRevenue,
          totalTimeMinutes: serviceData.durationMinutes,
        });
      }
    });
  });

  return {
    serviceStats,
    totalVariableCosts,
    totalRevenue,
    totalPointsEarned,
    totalPointsRedeemed,
  };
};

export const calculateBreakEvenMetrics = (
  fixedCosts: number,
  totalVariableCosts: number,
  totalRevenue: number,
): BreakEvenMetrics => {
  const currentProfit = totalRevenue - totalVariableCosts - fixedCosts;
  const missingProfit = currentProfit < 0 ? Math.abs(currentProfit) : 0;

  return {
    fixedCosts,
    variableCosts: totalVariableCosts,
    currentRevenue: totalRevenue,
    currentProfit,
    missingProfit,
  };
};

export const calculatePriceIncreaseMetrics = (
  fixedCosts: number,
  totalRevenue: number,
  totalVariableCosts: number,
  serviceStats: ServiceStats,
): PriceIncreaseMetrics => {
  const requiredRevenue = fixedCosts + totalVariableCosts;
  const revenueDifference = Math.max(0, requiredRevenue - totalRevenue);
  const totalServicesPerformed = Array.from(serviceStats.values()).reduce(
    (sum, stats) => sum + stats.count,
    0,
  );

  const averagePriceIncrease =
    totalServicesPerformed > 0 ? revenueDifference / totalServicesPerformed : 0;

  const serviceSpecificIncreases = Array.from(serviceStats.values()).map(stats => {
    const averageActualPrice = stats.totalRevenue / stats.count;
    const avgVariableCost = stats.totalVariableCost / stats.count;
    const currentProfit = averageActualPrice - avgVariableCost;

    const profitMargin = averageActualPrice > 0 ? currentProfit / averageActualPrice : 0;
    const adjustmentFactor = profitMargin < 0.3 ? 1.5 : profitMargin < 0.5 ? 1.0 : 0.7;

    const increaseAmount = averagePriceIncrease * adjustmentFactor;
    const suggestedPrice = averageActualPrice + increaseAmount;
    const increasePercentage =
      averageActualPrice > 0 ? (increaseAmount / averageActualPrice) * 100 : 0;

    return {
      serviceId: stats.service.id,
      serviceName: stats.service.name,
      currentPrice: averageActualPrice,
      suggestedPrice,
      increaseAmount,
      increasePercentage,
      timesPerformed: stats.count,
    };
  });

  return {
    requiredRevenue,
    currentRevenue: totalRevenue,
    revenueDifference,
    totalServicesPerformed,
    averagePriceIncrease,
    priceIncreasePercentage:
      totalRevenue > 0 ? (averagePriceIncrease / (totalRevenue / totalServicesPerformed)) * 100 : 0,
    serviceSpecificIncreases,
  };
};

export const calculateProfitPerHourMetrics = (serviceStats: ServiceStats) => {
  const profitPerHourMetrics: ProfitPerHourMetrics[] = [];
  const profitPerHourExplicit: ProfitPerHourExplicit[] = [];

  serviceStats.forEach(stats => {
    const avgVariableCost = stats.totalVariableCost / stats.count;
    const profit = stats.service.price - avgVariableCost;
    const durationHours = stats.service.durationMinutes / 60;
    const profitPerHour = durationHours > 0 ? profit / durationHours : 0;

    profitPerHourMetrics.push({
      serviceId: stats.service.id,
      serviceName: stats.service.name,
      price: stats.service.price,
      variableCost: avgVariableCost,
      profit,
      durationMinutes: stats.service.durationMinutes,
      durationHours,
      profitPerHour,
      timesPerformed: stats.count,
      totalProfit: profit * stats.count,
    });

    profitPerHourExplicit.push({
      serviceId: stats.service.id,
      serviceName: stats.service.name,
      profit,
      profitPerHour,
      durationMinutes: stats.service.durationMinutes,
      price: stats.service.price,
      variableCost: avgVariableCost,
      timesPerformed: stats.count,
      totalProfit: profit * stats.count,
      totalRevenue: stats.totalRevenue,
    });
  });

  profitPerHourMetrics.sort((a, b) => b.profitPerHour - a.profitPerHour);
  profitPerHourExplicit.sort((a, b) => b.profitPerHour - a.profitPerHour);

  return { profitPerHourMetrics, profitPerHourExplicit };
};

export const calculateVariableCostsMetrics = (
  serviceStats: ServiceStats,
): VariableCostMetrics[] => {
  return Array.from(serviceStats.values()).map(stats => {
    const avgVariableCost = stats.totalVariableCost / stats.count;
    const profitMargin = stats.service.price - avgVariableCost;
    const profitMarginPercentage =
      stats.service.price > 0 ? (profitMargin / stats.service.price) * 100 : 0;

    return {
      serviceId: stats.service.id,
      serviceName: stats.service.name,
      variableCost: avgVariableCost,
      timesPerformed: stats.count,
      totalVariableCost: stats.totalVariableCost,
      price: stats.service.price,
      profitMargin,
      profitMarginPercentage,
    };
  });
};

export const calculateInefficiencyAnalysis = (
  serviceStats: ServiceStats,
  totalRevenue: number,
  totalVariableCosts: number,
  topProfitPerHour: number,
): InefficiencyAnalysis[] => {
  const totalTimeSpent = Array.from(serviceStats.values()).reduce(
    (sum, stats) => sum + stats.totalTimeMinutes,
    0,
  );
  const totalProfit = totalRevenue - totalVariableCosts;

  const analysis = Array.from(serviceStats.values()).map(stats => {
    const timeSharePercentage =
      totalTimeSpent > 0 ? (stats.totalTimeMinutes / totalTimeSpent) * 100 : 0;
    const serviceProfit = stats.totalRevenue - stats.totalVariableCost;
    const profitSharePercentage = totalProfit > 0 ? (serviceProfit / totalProfit) * 100 : 0;
    const inefficiencyPercentage = timeSharePercentage - profitSharePercentage;

    const idealProfit = topProfitPerHour * (stats.totalTimeMinutes / 60);
    const financialLoss = Math.max(0, idealProfit - serviceProfit);

    const durationHours = stats.service.durationMinutes / 60;
    const avgVariableCost = stats.totalVariableCost / stats.count;
    const profit = stats.service.price - avgVariableCost;
    const profitPerHour = durationHours > 0 ? profit / durationHours : 0;

    let recommendation: InefficiencyAnalysis['recommendation'] = 'optimal';
    if (inefficiencyPercentage > 20) {
      recommendation = 'very-inefficient';
    } else if (inefficiencyPercentage > 10) {
      recommendation = 'inefficient';
    } else if (inefficiencyPercentage > 5) {
      recommendation = 'acceptable';
    }

    return {
      serviceId: stats.service.id,
      serviceName: stats.service.name,
      timeSpentMinutes: stats.totalTimeMinutes,
      timeSharePercentage,
      profit: serviceProfit,
      profitSharePercentage,
      inefficiencyPercentage,
      financialLoss,
      timesPerformed: stats.count,
      profitPerHour,
      recommendation,
    };
  });

  return analysis.sort((a, b) => b.inefficiencyPercentage - a.inefficiencyPercentage);
};

export const calculateLoyaltyPointsReport = (
  totalPointsRedeemed: number,
  totalPointsEarned: number,
  totalRevenue: number,
  filteredVisits: VisitDetail[],
): LoyaltyPointsReport => {
  const visitsWithPointsRedeemed = filteredVisits.filter(
    ({ loyalty }) => (loyalty.loyaltyPointsUsed || 0) > 0,
  ).length;

  return {
    totalRedeemedValue: totalPointsRedeemed,
    totalEarnedValue: totalPointsEarned,
    totalRevenue,
    redeemedToRevenueRatio: totalRevenue > 0 ? (totalPointsRedeemed / totalRevenue) * 100 : 0,
    visitsWithPointsRedeemed,
    totalVisits: filteredVisits.length,
    visitsWithPointsRedeemedPercentage:
      filteredVisits.length > 0 ? (visitsWithPointsRedeemed / filteredVisits.length) * 100 : 0,
    averagePointsRedeemedPerVisit:
      filteredVisits.length > 0 ? totalPointsRedeemed / filteredVisits.length : 0,
    averagePointsEarnedPerVisit:
      filteredVisits.length > 0 ? totalPointsEarned / filteredVisits.length : 0,
    netLoyaltyImpact: totalPointsEarned - totalPointsRedeemed,
  };
};

export const calculateServiceDetailedMetrics = (
  serviceStats: ServiceStats,
  fixedCosts: number,
  totalRevenue: number,
  totalVariableCosts: number,
): ServiceDetailedMetrics[] => {
  const totalTimeSpent = Array.from(serviceStats.values()).reduce(
    (sum, stats) => sum + stats.totalTimeMinutes,
    0,
  );

  return Array.from(serviceStats.values()).map(stats => {
    const avgVariableCost = stats.totalVariableCost / stats.count;
    const currentPrice = stats.service.price;
    const averageActualPrice = stats.totalRevenue / stats.count;
    const timesPerformed = stats.count;
    const durationMinutes = stats.service.durationMinutes;

    const actualContributionMargin = averageActualPrice - avgVariableCost;
    const actualContributionMarginPercentage =
      averageActualPrice > 0 ? (actualContributionMargin / averageActualPrice) * 100 : 0;

    const durationHours = durationMinutes / 60;
    const profitPerHour = durationHours > 0 ? actualContributionMargin / durationHours : 0;
    const totalProfit = actualContributionMargin * timesPerformed;

    const serviceTimeShare = totalTimeSpent > 0 ? stats.totalTimeMinutes / totalTimeSpent : 0;

    const serviceFixedCostShare = fixedCosts * serviceTimeShare;
    const currentServiceContribution = actualContributionMargin * timesPerformed;
    const missingContributionIndividual = Math.max(
      0,
      serviceFixedCostShare - currentServiceContribution,
    );

    const priceIncreaseIndividual =
      timesPerformed > 0 ? missingContributionIndividual / timesPerformed : 0;
    const priceIncreaseIndividualPercentage =
      averageActualPrice > 0 ? (priceIncreaseIndividual / averageActualPrice) * 100 : 0;
    const breakEvenPriceIndividual = averageActualPrice + priceIncreaseIndividual;

    const additionalVisitsIndividualNeeded =
      actualContributionMargin > 0
        ? Math.ceil(missingContributionIndividual / actualContributionMargin)
        : 0;

    const breakEvenTotalVisitsIndividual = timesPerformed + additionalVisitsIndividualNeeded;

    const revenueShare = totalRevenue > 0 ? (stats.totalRevenue / totalRevenue) * 100 : 0;
    const totalProfitAllServices = totalRevenue - totalVariableCosts;
    const profitShare =
      totalProfitAllServices > 0 ? (totalProfit / totalProfitAllServices) * 100 : 0;
    const timeShare = totalTimeSpent > 0 ? (stats.totalTimeMinutes / totalTimeSpent) * 100 : 0;

    return {
      serviceId: stats.service.id,
      serviceName: stats.service.name,
      currentPrice,
      averageActualPrice,
      currentTimesPerformed: timesPerformed,
      durationMinutes,
      averageVariableCost: avgVariableCost,
      contributionMargin: actualContributionMargin,
      contributionMarginPercentage: actualContributionMarginPercentage,
      profitPerHour,
      totalProfit,
      individual: {
        missingContributionIndividual,
        priceIncreaseIndividual,
        priceIncreaseIndividualPercentage,
        additionalVisitsIndividualNeeded,
        breakEvenPriceIndividual,
        breakEvenTotalVisitsIndividual,
      },
      revenueShare,
      profitShare,
      timeShare,
    };
  });
};
